import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Plus, Trash2, HelpCircle } from "lucide-react";
import type { ToolType } from "../../../types";

const FIXED_NEW_POSITIONS = [
  { id: "new", displayName: "Nuevo" },
  { id: "delivered", displayName: "Entregado" },
  { id: "read", displayName: "Leido" },
  { id: "answered", displayName: "Respondido" },
];

const FIXED_PENDING_POSITIONS = [{ id: "redirected", displayName: "Redireccion humano" }];

const FIXED_SUCCESS_POSITIONS: { id: string; displayName: string }[] = [];

const FIXED_FAILURE_POSITIONS = [{ id: "inactive", displayName: "Inactivo" }];

const STAGE_TOOLTIPS: Record<string, string> = {
  Nuevo: "En esta etapa la conversacion esta siendo manejada por el agente IA",
  Pendiente:
    "La conversacion avanzo de etapa y requiere la atencion y/o accion de un agente humano.",
  Exito:
    "Usuario convertido o conversacion finalizada exitosamente en el proceso de cartera.",
  Fallido: "Tambien es una etapa final de la conversacion sin exito.",
};

const POSITION_TOOLTIPS: Record<string, string> = {
  new: "Conversacion iniciada, aun sin confirmacion de entrega del primer mensaje al usuario.",
  delivered: "Confirmacion de whatsapp de que el primer mensaje ha sido entregado al usuario.",
  read: "Confirmacion de whatsapp de que el primer mensaje ha sido leido por el usuario.",
  answered:
    "Usuario ha interactuado al menos una vez en la conversacion (o respondio el primer mensaje de la conversacion).",
  redirected:
    "El agente IA detecta que la solicitud del cliente requiere la atencion de un agente humano.",
  inactive: 'Se cumplio el tiempo de inactividad estando en la etapa "Nuevo".',
};

const CARTERA_TOOLS = [
  { value: "transferConversation", label: "Transferir Conversacion" },
  { value: "updatePipelinePosition", label: "Actualizar estado del usuario" },
] as const;

const ALL_TOOLS = [
  ...CARTERA_TOOLS,
  { value: "scheduleVisit", label: "Agendar Visita" },
  { value: "getProperty", label: "Obtener Propiedad" },
  { value: "getPropertyByCode", label: "Obtener Propiedad por Codigo" },
  { value: "searchProperties", label: "Buscar Propiedades" },
  { value: "assignAgent", label: "Asignar Agente" },
  { value: "setEmail", label: "Correo electrónico" },
  { value: "registerListingRequest", label: "Registrar Solicitud de Publicacion" },
  { value: "discardLead", label: "Descartar Lead" },
  { value: "calculateDaysFor", label: "Calcular Dias" },
  { value: "createMaintenanceRequest", label: "Registrar Solicitud de Mantenimiento" },
  { value: "getCustomerProperties", label: "Obtener inmuebles del cliente" },
  { value: "getCustomerDocuments", label: "Obtener documentos del cliente" },
  { value: "createServiceRequest", label: "Crear solicitud de servicio" },
  { value: "updateServiceRequest", label: "Actualizar solicitud de servicio" },
  { value: "lookupByContractNumber", label: "Buscar por Numero de Contrato" },
  { value: "setAppointmentIntention", label: "Registrar intencion de cita" },
  { value: "sendMedia", label: "Enviar multimedia" },
] as const;

const HUMAN_ASSIGNMENT_TOOLTIP =
  "La asignacion y notificacion se genera unicamente si el cambio a este estado es generado por el agente IA";

export type CarteraAgentFormData = {
  agentName: string;
  systemPrompt: string;
  pendingPositions: { id: string; displayName: string; humanAssignmentEnabled?: boolean }[];
  successPositions: { id: string; displayName: string; humanAssignmentEnabled?: boolean }[];
  failurePositions: { id: string; displayName: string; humanAssignmentEnabled?: boolean }[];
  selectedTools: ToolType[];
};

type Step2CarteraConfigurationProps = {
  formData: CarteraAgentFormData;
  onFormDataChange: (data: CarteraAgentFormData) => void;
};

const Step2CarteraConfiguration = ({
  formData,
  onFormDataChange,
}: Step2CarteraConfigurationProps) => {
  const addPendingPosition = () => {
    onFormDataChange({
      ...formData,
      pendingPositions: [
        ...formData.pendingPositions,
        { id: "", displayName: "", humanAssignmentEnabled: false },
      ],
    });
  };

  const removePendingPosition = (index: number) => {
    onFormDataChange({
      ...formData,
      pendingPositions: formData.pendingPositions.filter((_, i) => i !== index),
    });
  };

  const updatePendingPosition = (
    index: number,
    field: "id" | "displayName" | "humanAssignmentEnabled",
    value: string | boolean
  ) => {
    const next = [...formData.pendingPositions];
    next[index] = { ...next[index], [field]: value };
    onFormDataChange({ ...formData, pendingPositions: next });
  };

  const addSuccessPosition = () => {
    onFormDataChange({
      ...formData,
      successPositions: [
        ...formData.successPositions,
        { id: "", displayName: "", humanAssignmentEnabled: false },
      ],
    });
  };

  const removeSuccessPosition = (index: number) => {
    onFormDataChange({
      ...formData,
      successPositions: formData.successPositions.filter((_, i) => i !== index),
    });
  };

  const updateSuccessPosition = (
    index: number,
    field: "id" | "displayName" | "humanAssignmentEnabled",
    value: string | boolean
  ) => {
    const next = [...formData.successPositions];
    next[index] = { ...next[index], [field]: value };
    onFormDataChange({ ...formData, successPositions: next });
  };

  const addFailurePosition = () => {
    onFormDataChange({
      ...formData,
      failurePositions: [
        ...formData.failurePositions,
        { id: "", displayName: "", humanAssignmentEnabled: false },
      ],
    });
  };

  const removeFailurePosition = (index: number) => {
    onFormDataChange({
      ...formData,
      failurePositions: formData.failurePositions.filter((_, i) => i !== index),
    });
  };

  const updateFailurePosition = (
    index: number,
    field: "id" | "displayName" | "humanAssignmentEnabled",
    value: string | boolean
  ) => {
    const next = [...formData.failurePositions];
    next[index] = { ...next[index], [field]: value };
    onFormDataChange({ ...formData, failurePositions: next });
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg font-semibold mb-4">
        Configuracion del Agente de Cartera
      </p>

      {/* Pipeline info - read-only */}
      <div>
        <p className="text-base font-semibold mb-3">
          Proceso (ciclo de vida de las conversaciones)
        </p>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">ID del proceso</Label>
            <Input value="collections" readOnly className="bg-gray-50" />
          </div>
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">Nombre del proceso (para mostrar)</Label>
            <Input value="Cartera" readOnly className="bg-gray-50" />
          </div>
        </div>
      </div>

      {/* Pipeline view -- one column per stage */}
      <div>
        <p className="text-base font-semibold mb-3">
          Etapas del proceso
        </p>
        <p className="text-sm text-gray-600 mb-3">
          Las etapas pueden tener diferentes posiciones. Puedes anadir posiciones personalizadas
          para las etapas "Pendiente", "Exito" y "Fallido".
        </p>
        <div className="border border-gray-200 rounded-md bg-gray-50 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ minWidth: 0 }}>
            {/* New stage column */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold text-gray-700">
                  Nuevo
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help" aria-label="Mas informacion">
                        <HelpCircle className="size-3.5 text-gray-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px]">
                      {STAGE_TOOLTIPS.Nuevo}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {FIXED_NEW_POSITIONS.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md"
                >
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-gray-700">
                      {p.displayName}
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-help" aria-label="Mas informacion">
                            <HelpCircle className="size-3.5 text-gray-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {POSITION_TOOLTIPS[p.id] ?? ""}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
            </div>

            {/* Pending stage column */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold text-gray-700">
                  Pendiente
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help" aria-label="Mas informacion">
                        <HelpCircle className="size-3.5 text-gray-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[320px]">
                      {STAGE_TOOLTIPS.Pendiente}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {FIXED_PENDING_POSITIONS.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md"
                >
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-gray-700">
                      {p.displayName}
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-help" aria-label="Mas informacion">
                            <HelpCircle className="size-3.5 text-gray-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {POSITION_TOOLTIPS[p.id] ?? ""}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
              {formData.pendingPositions.map((pos, index) => (
                <div
                  key={index}
                  className="p-2 bg-white border border-blue-200 rounded-md"
                >
                  <div className="flex flex-col gap-1">
                    <Input
                      placeholder="id (ej: enRevision)"
                      value={pos.id}
                      className="h-7 text-xs"
                      onChange={(e) =>
                        updatePendingPosition(
                          index,
                          "id",
                          e.target.value.replace(/\s/g, "").replace(/([a-z])([A-Z])/g, "$1$2")
                        )
                      }
                    />
                    <Input
                      placeholder="Nombre (ej: En revision)"
                      value={pos.displayName}
                      className="h-7 text-xs"
                      onChange={(e) => updatePendingPosition(index, "displayName", e.target.value)}
                    />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                className="h-3.5 w-3.5"
                                checked={pos.humanAssignmentEnabled ?? false}
                                onCheckedChange={(checked) =>
                                  updatePendingPosition(index, "humanAssignmentEnabled", checked === true)
                                }
                              />
                              <label className="text-xs">
                                Asignacion humana
                              </label>
                            </div>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {HUMAN_ASSIGNMENT_TOOLTIP}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 text-xs"
                      onClick={() => removePendingPosition(index)}
                    >
                      <Trash2 className="size-3 mr-1" />
                      Quitar
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
                onClick={addPendingPosition}
              >
                <Plus className="size-4 mr-1" />
                Anadir posicion
              </Button>
            </div>

            {/* Success stage column */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold text-gray-700">
                  Exito
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help" aria-label="Mas informacion">
                        <HelpCircle className="size-3.5 text-gray-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px]">
                      {STAGE_TOOLTIPS.Exito}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {FIXED_SUCCESS_POSITIONS.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md"
                >
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-gray-700">
                      {p.displayName}
                    </p>
                  </div>
                </div>
              ))}
              {formData.successPositions.map((pos, index) => (
                <div
                  key={index}
                  className="p-2 bg-white border border-green-200 rounded-md"
                >
                  <div className="flex flex-col gap-1">
                    <Input
                      placeholder="id (ej: pagoRealizado)"
                      value={pos.id}
                      className="h-7 text-xs"
                      onChange={(e) =>
                        updateSuccessPosition(
                          index,
                          "id",
                          e.target.value.replace(/\s/g, "").replace(/([a-z])([A-Z])/g, "$1$2")
                        )
                      }
                    />
                    <Input
                      placeholder="Nombre (ej: Pago realizado)"
                      value={pos.displayName}
                      className="h-7 text-xs"
                      onChange={(e) => updateSuccessPosition(index, "displayName", e.target.value)}
                    />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                className="h-3.5 w-3.5"
                                checked={pos.humanAssignmentEnabled ?? false}
                                onCheckedChange={(checked) =>
                                  updateSuccessPosition(index, "humanAssignmentEnabled", checked === true)
                                }
                              />
                              <label className="text-xs">
                                Asignacion humana
                              </label>
                            </div>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {HUMAN_ASSIGNMENT_TOOLTIP}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 text-xs"
                      onClick={() => removeSuccessPosition(index)}
                    >
                      <Trash2 className="size-3 mr-1" />
                      Quitar
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                className="w-full text-green-600 border-green-300 hover:bg-green-50"
                onClick={addSuccessPosition}
              >
                <Plus className="size-4 mr-1" />
                Anadir posicion
              </Button>
            </div>

            {/* Failure stage column */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold text-gray-700">
                  Fallido
                </p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help" aria-label="Mas informacion">
                        <HelpCircle className="size-3.5 text-gray-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[280px]">
                      {STAGE_TOOLTIPS.Fallido}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {FIXED_FAILURE_POSITIONS.map((p) => (
                <div
                  key={p.id}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md"
                >
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium text-gray-700">
                      {p.displayName}
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-help" aria-label="Mas informacion">
                            <HelpCircle className="size-3.5 text-gray-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {POSITION_TOOLTIPS[p.id] ?? ""}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              ))}
              {formData.failurePositions.map((pos, index) => (
                <div
                  key={index}
                  className="p-2 bg-white border border-red-200 rounded-md"
                >
                  <div className="flex flex-col gap-1">
                    <Input
                      placeholder="id (ej: noContactar)"
                      value={pos.id}
                      className="h-7 text-xs"
                      onChange={(e) =>
                        updateFailurePosition(
                          index,
                          "id",
                          e.target.value.replace(/\s/g, "").replace(/([a-z])([A-Z])/g, "$1$2")
                        )
                      }
                    />
                    <Input
                      placeholder="Nombre (ej: No contactar)"
                      value={pos.displayName}
                      className="h-7 text-xs"
                      onChange={(e) => updateFailurePosition(index, "displayName", e.target.value)}
                    />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                className="h-3.5 w-3.5"
                                checked={pos.humanAssignmentEnabled ?? false}
                                onCheckedChange={(checked) =>
                                  updateFailurePosition(index, "humanAssignmentEnabled", checked === true)
                                }
                              />
                              <label className="text-xs">
                                Asignacion humana
                              </label>
                            </div>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[280px]">
                          {HUMAN_ASSIGNMENT_TOOLTIP}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 text-xs"
                      onClick={() => removeFailurePosition(index)}
                    >
                      <Trash2 className="size-3 mr-1" />
                      Quitar
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                size="sm"
                variant="outline"
                className="w-full text-red-600 border-red-300 hover:bg-red-50"
                onClick={addFailurePosition}
              >
                <Plus className="size-4 mr-1" />
                Anadir posicion
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Agent name */}
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium">
          Nombre del agente <span className="text-red-500">*</span>
        </Label>
        <Input
          value={formData.agentName}
          onChange={(e) => onFormDataChange({ ...formData, agentName: e.target.value })}
          placeholder="Ingresa el nombre del agente"
        />
      </div>

      {/* Prompt - empty by default, not required */}
      <div className="flex flex-col gap-2">
        <Label className="text-sm font-medium">Instrucciones del agente</Label>
        <Textarea
          value={formData.systemPrompt}
          onChange={(e) => onFormDataChange({ ...formData, systemPrompt: e.target.value })}
          placeholder="Describe el rol y las instrucciones del agente (opcional)"
          className="h-[70vh] min-h-[400px] resize-y overflow-y-auto text-sm font-mono p-4"
        />
      </div>

      {/* Tools */}
      <div>
        <p className="text-base font-semibold mb-4">
          Herramientas
        </p>
        <div className="flex flex-col gap-2">
          {ALL_TOOLS.map((tool) => (
            <div key={tool.value} className="flex items-center gap-2">
              <Checkbox
                checked={formData.selectedTools?.includes(tool.value as ToolType) ?? false}
                onCheckedChange={(checked) => {
                  const currentTools = formData.selectedTools || [];
                  const toolValue = tool.value as ToolType;
                  const newTools = checked
                    ? [...currentTools, toolValue]
                    : currentTools.filter((t) => t !== toolValue);
                  onFormDataChange({ ...formData, selectedTools: newTools });
                }}
              />
              <label className="text-sm">{tool.label}</label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Step2CarteraConfiguration;
